import api from './api';

export interface KBDocument {
  document_id: string;
  relevance: string | number;
  added_date: string;
}

export interface KBDocumentDetails {
  id: string;
  reference: string;
  title?: string;
  display_name: string;
  full_filename: string;
  upload_date: string;
  author: string;
  size: string;
  document_type: string;
  patent_id?: string | null;
  person?: string;
  pub_month_year?: string;
  relevance?: string | number;
  justification?: string;
  stale?: boolean;
  added_date?: string;
}

export interface KnowledgeBase {
  id: string;
  name: string;
  owner_first_name: string;
  owner_family_name: string;
  owner_full_name?: string;
  project_id?: string | null;
  project_name?: string | null;
  sub_project_id?: string | null;
  sub_project_name?: string | null;
  documents?: KBDocument[];
  description?: string | null;
  visibility: 'private' | 'shared' | 'common';
  kb_class?: 'standard' | 'reference_large';
  status: string;
  document_count: number;
  created_date: string;
  last_updated: string;
}

export interface OrphanKnowledgeBase {
  kb: KnowledgeBase;
  orphan_reason: 'missing_client_id' | 'dangling_client_id' | string;
}

export interface CreateKnowledgeBaseRequest {
  name: string;
  owner_id: string;
  project_id?: string | null;
  sub_project_id?: string | null;
  description?: string | null;
  context?: string;
  visibility?: 'private' | 'shared' | 'common';
  kb_class?: 'standard' | 'reference_large';
}

export interface UpdateKnowledgeBaseRequest {
  name?: string;
  documents?: KBDocument[];
}

export const knowledgeBaseService = {
  async getAll(): Promise<KnowledgeBase[]> {
    const response = await api.get<KnowledgeBase[]>('/knowledge-bases/');
    return response.data;
  },

  async getOrphans(): Promise<OrphanKnowledgeBase[]> {
    const response = await api.get<OrphanKnowledgeBase[]>('/knowledge-bases/admin/orphans');
    return response.data;
  },

  async deleteOrphan(kbId: string, purgeRag: boolean = true): Promise<{status: string; kb_id: string; kb_name: string}> {
    const response = await api.delete<{status: string; kb_id: string; kb_name: string}>(
      `/knowledge-bases/admin/orphans/${kbId}?purge_rag=${purgeRag ? 'true' : 'false'}`
    );
    return response.data;
  },

  async getById(id: string): Promise<KnowledgeBase> {
    const response = await api.get<KnowledgeBase>(`/knowledge-bases/${id}`);
    return response.data;
  },

  async create(data: CreateKnowledgeBaseRequest): Promise<KnowledgeBase> {
    const response = await api.post<KnowledgeBase>('/knowledge-bases/', data);
    return response.data;
  },

  async update(id: string, data: UpdateKnowledgeBaseRequest): Promise<KnowledgeBase> {
    const response = await api.put<KnowledgeBase>(`/knowledge-bases/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/knowledge-bases/${id}`);
  },

  async getDocuments(kbId: string): Promise<KBDocument[]> {
    const response = await api.get<KBDocument[]>(`/knowledge-bases/${kbId}/documents`);
    return response.data;
  },

  async getDocumentsWithDetails(
    kbId: string,
    skip: number = 0,
    limit: number = 100,
    search?: string,
    context?: string,
    solutionId?: string | null
  ): Promise<{
    kb_id: string;
    kb_name: string;
    documents: KBDocumentDetails[];
    total_count: number;
    returned_count: number;
    skip: number;
    limit: number;
  }> {
    const params = new URLSearchParams();
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());
    if (search) {
      params.append('search', search);
    }
    if (context) {
      params.append('context', context);
    }
    if (solutionId) {
      params.append('solution_id', solutionId);
    }
    const response = await api.get<{
      kb_id: string;
      kb_name: string;
      documents: KBDocumentDetails[];
      total_count: number;
      returned_count: number;
      skip: number;
      limit: number;
    }>(`/knowledge-bases/${kbId}/documents/details?${params.toString()}`);
    return response.data;
  },

  async addDocument(kbId: string, documentId: string, relevance: string = 'medium'): Promise<{message: string; document_id: string; relevance: string}> {
    const response = await api.post<{message: string; document_id: string; relevance: string}>(`/knowledge-bases/${kbId}/documents/${documentId}?relevance=${relevance}`);
    return response.data;
  },

  async bulkAddDocuments(kbId: string, documentIds: string[], relevance: string = 'medium'): Promise<{added: number; skipped: number; kb_id: string}> {
    const response = await api.post<{added: number; skipped: number; kb_id: string}>(`/knowledge-bases/${kbId}/documents/bulk-add`, { document_ids: documentIds, relevance });
    return response.data;
  },

  async removeDocument(kbId: string, documentId: string): Promise<{message: string}> {
    const response = await api.delete<{message: string}>(`/knowledge-bases/${kbId}/documents/${documentId}`);
    return response.data;
  },

  async rescore(
    kbId: string,
    subProjectId: string,
    context?: string,
    solutionId?: string | null
  ): Promise<{kb_id: string; sub_project_id: string; context: string; solution_id: string | null; scored: number; reused: number}> {
    const params = new URLSearchParams({ sub_project_id: subProjectId });
    if (context) params.append('context', context);
    if (solutionId) params.append('solution_id', solutionId);
    const response = await api.post<{kb_id: string; sub_project_id: string; context: string; solution_id: string | null; scored: number; reused: number}>(
      `/knowledge-bases/${kbId}/rescore?${params.toString()}`
    );
    return response.data;
  },

  async updateDocumentRelevance(
    kbId: string,
    documentId: string,
    score: number,
    context: string = 'state_of_the_art',
    solutionId?: string | null,
    justification?: string
  ): Promise<{message: string; document_id: string; score: number}> {
    const response = await api.put<{message: string; document_id: string; score: number}>(
      `/knowledge-bases/${kbId}/documents/${documentId}/relevance`,
      { score, context, solution_id: solutionId ?? null, justification: justification ?? null }
    );
    return response.data;
  },
};
